import {
  KioskBrandingLogoDto,
  KioskSettingsDto,
  KnownKioskBrandingDtoPoweredByTheme,
  KnownKioskBrandingLogoDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { KioskThemeSettingsForm, ThemeZone } from './form';

const toZone = (dark: boolean): ThemeZone => (dark ? 'dark' : 'light');
const fromZone = (zone: ThemeZone): boolean => zone === 'dark';

export class KioskThemeSettingsFormOutput {
  themeHome: ThemeZone;
  themeOtherScreens: ThemeZone;
  themeHomeFooter: ThemeZone;
  themeSessionFooter: ThemeZone;
  poweredByTheme: string;
  logoType: string;
  logoSrc: string;
  logoHomeScreenHeightPx?: number;
  logoHomeScreenPositionYPx?: number;
  backgroundHome: string;
  backgroundSession: string;
  colorsLightPrimary: string;
  colorsLightPrimaryVariant: string;
  colorsLightOnPrimary: string;
  colorsLightOnSecondary: string;
  colorsDarkPrimary: string;
  colorsDarkPrimaryVariant: string;
  colorsDarkOnPrimary: string;
  colorsDarkOnSecondary: string;

  constructor(form: Partial<KioskThemeSettingsForm>) {
    this.themeHome = form.themeHome ?? 'dark';
    this.themeOtherScreens = form.themeOtherScreens ?? 'dark';
    this.themeHomeFooter = form.themeHomeFooter ?? this.themeHome;
    this.themeSessionFooter = form.themeSessionFooter ?? this.themeOtherScreens;
    this.poweredByTheme = form.poweredByTheme ?? '';
    this.logoType = form.logoType ?? '';
    this.logoSrc = form.logoSrc ?? '';
    this.logoHomeScreenHeightPx = form.logoHomeScreenHeightPx;
    this.logoHomeScreenPositionYPx = form.logoHomeScreenPositionYPx;
    this.backgroundHome = form.backgroundHome ?? '';
    this.backgroundSession = form.backgroundSession ?? '';
    this.colorsLightPrimary = form.colorsLightPrimary ?? '';
    this.colorsLightPrimaryVariant = form.colorsLightPrimaryVariant ?? '';
    this.colorsLightOnPrimary = form.colorsLightOnPrimary ?? '';
    this.colorsLightOnSecondary = form.colorsLightOnSecondary ?? '';
    this.colorsDarkPrimary = form.colorsDarkPrimary ?? '';
    this.colorsDarkPrimaryVariant = form.colorsDarkPrimaryVariant ?? '';
    this.colorsDarkOnPrimary = form.colorsDarkOnPrimary ?? '';
    this.colorsDarkOnSecondary = form.colorsDarkOnSecondary ?? '';
  }

  static FromDto(dto: KioskSettingsDto): KioskThemeSettingsForm {
    const b = dto.branding;
    const darkTheme = b?.darkTheme ?? true;
    const darkThemeHomeButtons = b?.darkThemeHomeButtons ?? darkTheme;

    return {
      themeHome: toZone(darkThemeHomeButtons),
      themeOtherScreens: toZone(darkTheme),
      themeHomeFooter: toZone(b?.darkThemeHomeFooter ?? darkThemeHomeButtons),
      themeSessionFooter: toZone(b?.darkThemeFooter ?? darkTheme),
      poweredByTheme: b?.poweredByTheme ?? KnownKioskBrandingDtoPoweredByTheme.Mono,
      logoType: b?.logo?.type ?? KnownKioskBrandingLogoDtoType.None,
      logoSrc: b?.logo?.src ?? '',
      logoHomeScreenHeightPx: b?.logo?.homeScreenHeightPx,
      logoHomeScreenPositionYPx: b?.logo?.homeScreenPositionYPx,
      backgroundHome: b?.backgroundHome ?? '',
      backgroundSession: b?.backgroundSession ?? '',
      colorsLightPrimary: b?.colors?.light?.primary ?? '',
      colorsLightPrimaryVariant: b?.colors?.light?.primaryVariant ?? '',
      colorsLightOnPrimary: b?.colors?.light?.onPrimary ?? '',
      colorsLightOnSecondary: b?.colors?.light?.onSecondary ?? '',
      colorsDarkPrimary: b?.colors?.dark?.primary ?? '',
      colorsDarkPrimaryVariant: b?.colors?.dark?.primaryVariant ?? '',
      colorsDarkOnPrimary: b?.colors?.dark?.onPrimary ?? '',
      colorsDarkOnSecondary: b?.colors?.dark?.onSecondary ?? ''
    };
  }

  static ToDto(output: KioskThemeSettingsFormOutput): KioskSettingsDto {
    return {
      branding: {
        darkTheme: fromZone(output.themeOtherScreens),
        darkThemeFooter: fromZone(output.themeSessionFooter),
        darkThemeHomeButtons: fromZone(output.themeHome),
        darkThemeHomeFooter: fromZone(output.themeHomeFooter),
        poweredByTheme: output.poweredByTheme || undefined,
        backgroundHome: output.backgroundHome || undefined,
        backgroundSession: output.backgroundSession || undefined,
        logo: buildLogoDto(output),
        colors: {
          light: {
            primary: output.colorsLightPrimary || undefined,
            primaryVariant: output.colorsLightPrimaryVariant || undefined,
            onPrimary: output.colorsLightOnPrimary || undefined,
            onSecondary: output.colorsLightOnSecondary || undefined
          },
          dark: {
            primary: output.colorsDarkPrimary || undefined,
            primaryVariant: output.colorsDarkPrimaryVariant || undefined,
            onPrimary: output.colorsDarkOnPrimary || undefined,
            onSecondary: output.colorsDarkOnSecondary || undefined
          }
        }
      }
    };
  }
}

function buildLogoDto(output: KioskThemeSettingsFormOutput): KioskBrandingLogoDto {
  const isBranded = output.logoType === KnownKioskBrandingLogoDtoType.Branded;
  return {
    type: output.logoType || undefined,
    src: isBranded ? output.logoSrc || undefined : undefined,
    homeScreenHeightPx: isBranded ? output.logoHomeScreenHeightPx : undefined,
    homeScreenPositionYPx: isBranded ? output.logoHomeScreenPositionYPx : undefined
  };
}
